/**
 * GalleryLightbox, luxury full-screen image viewer.
 *
 * Mounted once at the EditorialArticlePage level. On mount it scans the
 * rendered article body for any `.lwd-gallery` and `.lwd-slider` containers
 * (produced by the Tiptap Gallery / SliderGallery nodes) and attaches a
 * click handler to each image. Clicking opens a full-screen modal with
 * prev/next arrows, a 1/N counter, a thumbnail strip, keyboard navigation
 * (←, →, Esc), and touch-swipe support on mobile.
 *
 * The Tiptap-rendered markup stays untouched, the enhancer is purely
 * additive, so legacy galleries from before this commit still render as
 * static grids and unhandled HTML doesn't break.
 */
import { useEffect, useState, useRef, useCallback } from 'react';

const GOLD = '#c9a96e';
const BG   = 'rgba(8, 7, 6, 0.96)';

// ─── Lightbox modal ─────────────────────────────────────────────────────────
function Lightbox({ images, startIdx, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  const touchX = useRef(null);

  const prev = useCallback(() => setIdx(i => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx(i => (i + 1) % images.length), [images.length]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft')  prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while lightbox is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, prev, next]);

  // Swipe nav
  const onTouchStart = (e) => { touchX.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd   = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) next(); else prev();
  };

  const img = images[idx] || images[0];
  if (!img) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        position: 'fixed', inset: 0, zIndex: 100000,
        background: BG,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        animation: 'lwd-lightbox-fade 240ms ease-out',
      }}
    >
      <style>{`
        @keyframes lwd-lightbox-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes lwd-lightbox-img-in {
          from { opacity: 0; transform: scale(0.98); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>

      {/* Top bar, counter + close */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'clamp(16px, 2.4vw, 32px) clamp(20px, 3vw, 48px)',
        zIndex: 2, pointerEvents: 'none',
      }}>
        <span style={{
          fontFamily: '"Jost", sans-serif',
          fontSize: 11,
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.7)',
          fontWeight: 500,
          pointerEvents: 'none',
        }}>
          {String(idx + 1).padStart(2, '0')} <span style={{ opacity: 0.4, margin: '0 8px' }}>/</span> {String(images.length).padStart(2, '0')}
        </span>

        <button
          aria-label="Close"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          style={{
            pointerEvents: 'auto',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '50%',
            width: 40, height: 40,
            color: '#fff',
            fontSize: 18,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.18s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
        >✕</button>
      </div>

      {/* Image stage */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: 'min(94vw, 1400px)',
          height: 'min(78vh, 900px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <img
          key={idx}
          src={img.src}
          alt={img.alt || ''}
          style={{
            maxWidth: '100%', maxHeight: '100%',
            objectFit: 'contain',
            display: 'block',
            boxShadow: '0 24px 96px rgba(0,0,0,0.6), 0 4px 24px rgba(0,0,0,0.3)',
            animation: 'lwd-lightbox-img-in 320ms cubic-bezier(0.2, 0.8, 0.2, 1)',
          }}
        />
        {img.caption && (
          <div style={{
            position: 'absolute',
            bottom: -56, left: 0, right: 0,
            textAlign: 'center',
            color: 'rgba(255,255,255,0.7)',
            fontFamily: '"Jost", sans-serif',
            fontSize: 12,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
          }}>
            {img.caption}
          </div>
        )}
      </div>

      {/* Prev / Next, only visible if more than one image */}
      {images.length > 1 && (
        <>
          <button
            aria-label="Previous"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            style={navArrowStyle('left')}
            onMouseEnter={(e) => { e.currentTarget.style.background = `rgba(${parseInt(GOLD.slice(1,3),16)},${parseInt(GOLD.slice(3,5),16)},${parseInt(GOLD.slice(5,7),16)},0.18)`; e.currentTarget.style.borderColor = GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
          >‹</button>
          <button
            aria-label="Next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            style={navArrowStyle('right')}
            onMouseEnter={(e) => { e.currentTarget.style.background = `rgba(${parseInt(GOLD.slice(1,3),16)},${parseInt(GOLD.slice(3,5),16)},${parseInt(GOLD.slice(5,7),16)},0.18)`; e.currentTarget.style.borderColor = GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
          >›</button>
        </>
      )}

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            bottom: 'clamp(20px, 3vw, 40px)',
            left: 0, right: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: 6,
            padding: '0 16px',
            overflowX: 'auto',
            scrollbarWidth: 'none',
          }}
        >
          {images.map((thumb, i) => (
            <button
              key={i}
              aria-label={`Go to image ${i + 1}`}
              onClick={() => setIdx(i)}
              style={{
                flex: '0 0 auto',
                width:  i === idx ? 56 : 40,
                height: 40,
                padding: 0,
                background: '#000',
                border: i === idx ? `1.5px solid ${GOLD}` : '1px solid rgba(255,255,255,0.12)',
                borderRadius: 2,
                overflow: 'hidden',
                cursor: 'pointer',
                opacity: i === idx ? 1 : 0.55,
                transition: 'all 0.22s',
              }}
            >
              <img
                src={thumb.src}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function navArrowStyle(side) {
  return {
    position: 'absolute',
    top: '50%',
    [side]: 'clamp(12px, 2.4vw, 28px)',
    transform: 'translateY(-50%)',
    width: 48, height: 48,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.15)',
    color: '#fff',
    fontSize: 24,
    fontWeight: 300,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.18s',
    zIndex: 2,
  };
}

// ─── Enhancer, scans body for galleries and adds click-to-open ─────────────
export default function GalleryLightbox({ scopeRef }) {
  const [open, setOpen] = useState(null);  // { images, startIdx } | null

  useEffect(() => {
    const root = scopeRef?.current || document;
    const galleries = Array.from(root.querySelectorAll('.lwd-gallery, .lwd-slider'));
    if (galleries.length === 0) return;

    // For each gallery, collect its images and wire click handlers per image
    const cleanups = [];
    galleries.forEach((galEl) => {
      const imgs = Array.from(galEl.querySelectorAll('img'));
      if (imgs.length === 0) return;
      const items = imgs.map((imgEl, i) => ({
        src:     imgEl.getAttribute('src') || '',
        alt:     imgEl.getAttribute('alt') || '',
        caption: galEl.querySelectorAll('figcaption')[i]?.textContent || '',
      }));

      imgs.forEach((imgEl, i) => {
        imgEl.style.cursor = 'zoom-in';
        const handler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen({ images: items, startIdx: i });
        };
        imgEl.addEventListener('click', handler);
        cleanups.push(() => imgEl.removeEventListener('click', handler));
      });
    });

    return () => cleanups.forEach(fn => fn());
  }, [scopeRef]);

  if (!open) return null;
  return (
    <Lightbox
      images={open.images}
      startIdx={open.startIdx}
      onClose={() => setOpen(null)}
    />
  );
}
